import { ShiftPlannerShift } from "@/components/Shared/ShiftPlanner";
import { APPLICATION_STATUS } from "@/configs/constants/ApplicationStatus";
import Competence from "@/models/Competence";
import { JobRequirements, Shift } from "@/models/Job";
import { MatchingConsultant } from "@/models/User";
import Organisation from "@/models/Organisation";
import {
	certificationTypeName,
	consultantTagName,
	isCredentialValid,
} from "@/utils/credential.utils";

/**
 * The credential constraint of the consultant panel's matching list:
 * certification typeKeys and consultant tag keys the consultant must ALL hold
 * validly (AND semantics — same as the job's matching gate and the consultant
 * list filter). Seeded from `job.requirements` and freely editable, so the
 * admin can relax it to surface almost-matching consultants and reset it back.
 */
export interface CredentialFilter {
	certifications: string[];
	tags: string[];
}

export function requirementsToCredentialFilter(requirements?: JobRequirements): CredentialFilter {
	return {
		certifications: (requirements?.certifications ?? []).map((c) => c.typeKey),
		tags: (requirements?.tags ?? []).map((t) => t.key),
	};
}

function sameIdSet(a: string[], b: string[]): boolean {
	return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

export function credentialFiltersEqual(a: CredentialFilter, b: CredentialFilter): boolean {
	return sameIdSet(a.certifications, b.certifications) && sameIdSet(a.tags, b.tags);
}

export function isEmptyCredentialFilter(filter: CredentialFilter): boolean {
	return filter.certifications.length === 0 && filter.tags.length === 0;
}

/**
 * The filter credentials the consultant does NOT validly hold. Expired entries
 * never count (same rule as the consultant list filter). Empty result = the
 * consultant satisfies the filter.
 */
export function missingCredentials(
	consultant: MatchingConsultant,
	filter: CredentialFilter
): CredentialFilter {
	const heldCertifications: { typeKey: string; expiresAt?: string | null }[] =
		consultant.profile?.certifications ?? [];
	const heldTags: { key: string; expiresAt?: string | null }[] = consultant.profile?.tags ?? [];

	return {
		certifications: filter.certifications.filter(
			(typeKey) =>
				!heldCertifications.some(
					(held) => held.typeKey === typeKey && isCredentialValid(held.expiresAt)
				)
		),
		tags: filter.tags.filter(
			(key) => !heldTags.some((held) => held.key === key && isCredentialValid(held.expiresAt))
		),
	};
}

/** Display names of a missing-credentials set, resolved from the org taxonomies. */
export function missingCredentialLabels(
	organisation: Organisation | undefined,
	missing: CredentialFilter
): string[] {
	return [
		...missing.certifications.map((typeKey) => certificationTypeName(organisation, typeKey)),
		...missing.tags.map((key) => consultantTagName(organisation, key)),
	];
}

/**
 * The competence constraint of the matching search: the job's
 * branch/service/skills, editable so the admin can drop a skill (or the whole
 * competence) to widen the match. `undefined` = no competence constraint.
 */
export interface CompetenceFilter {
	branch: string;
	service: string;
	skills: string[];
}

export function toCompetenceFilter(competence?: Competence): CompetenceFilter | undefined {
	if (!competence?.branch || competence.branch === "any") return undefined;
	return {
		branch: competence.branch,
		service: competence.service,
		skills: [...(competence.skills ?? [])],
	};
}

export function competenceFiltersEqual(a?: CompetenceFilter, b?: CompetenceFilter): boolean {
	if (!a || !b) return !a === !b;
	return a.branch === b.branch && a.service === b.service && sameIdSet(a.skills, b.skills);
}

/**
 * A consultant connected to the job in any way: booked or invited per the
 * CURRENT DRAFT (so unsaved planner edits are reflected), applied or
 * declined-an-invitation per the server shifts.
 */
export interface JobCandidate {
	id: string;
	name: string;
	bookedShiftIds: string[];
	appliedShiftIds: string[];
	invitedShiftIds: string[];
	declinedShiftIds: string[];
}

function candidateRank(candidate: JobCandidate): number {
	if (candidate.bookedShiftIds.length) return 0;
	if (candidate.appliedShiftIds.length) return 1;
	if (candidate.invitedShiftIds.length) return 2;
	return 3;
}

/**
 * Derives the "Kandidater" list: everyone booked/invited in the draft plus
 * everyone with a pending application or declined invitation on the server
 * shifts. Names resolve from `nameById` first (the planner roster), then from
 * the server-side consultant objects.
 */
export function deriveCandidates(
	draft: ShiftPlannerShift[],
	serverShifts: Shift[],
	nameById: Map<string, string>
): JobCandidate[] {
	const byId = new Map<string, JobCandidate>();

	function get(id: string, fallbackName?: string): JobCandidate {
		let candidate = byId.get(id);
		if (!candidate) {
			candidate = {
				id,
				name: nameById.get(id) || fallbackName || "–",
				bookedShiftIds: [],
				appliedShiftIds: [],
				invitedShiftIds: [],
				declinedShiftIds: [],
			};
			byId.set(id, candidate);
		}
		return candidate;
	}

	for (const shift of draft) {
		if (shift.consultantId) get(shift.consultantId).bookedShiftIds.push(shift.id);
		for (const id of shift.invitedConsultantIds ?? []) get(id).invitedShiftIds.push(shift.id);
	}

	for (const shift of serverShifts) {
		for (const application of shift.applications ?? []) {
			if (application.status !== APPLICATION_STATUS.PENDING_ANSWER) continue;
			const { id, firstName, lastName } = application.consultant;
			get(id, `${firstName} ${lastName}`).appliedShiftIds.push(shift.id);
		}
		for (const invitation of shift.invitations ?? []) {
			if (invitation.status !== "DECLINED") continue;
			const { id, firstName, lastName } = invitation.consultant;
			get(id, `${firstName} ${lastName}`).declinedShiftIds.push(shift.id);
		}
	}

	return [...byId.values()].sort(
		(a, b) => candidateRank(a) - candidateRank(b) || a.name.localeCompare(b.name, "sv")
	);
}
